import React, { useEffect, useState, useCallback } from 'react';
import {
  getClients, createClient, updateClient, deleteClient, changeClientPassword,
} from '../../services/api';
import { Modal, Input, Btn, Badge, toast, useConfirm, Card, Spinner } from '../../components/shared/UI';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clientStatus(c) {
  if (!c.active) return { label:'Inactivo', color:'red' };
  if (c.expiresAt && new Date(c.expiresAt) < new Date()) return { label:'Vencido', color:'yellow' };
  return { label:'Activo', color:'green' };
}

function storeUrl(clientId) {
  return `${window.location.origin}/tienda/${clientId}`;
}

// ─── Create client form ───────────────────────────────────────────────────────

function CreateForm({ onSave, onClose, saving }) {
  const [f, setF] = useState({ username:'', password:'', storeName:'', expiresAt:'' });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(f); }}>
      <Input label="Usuario *" value={f.username} onChange={e => set('username', e.target.value)} placeholder="cliente01" required />
      <Input label="Contraseña *" type="password" value={f.password} onChange={e => set('password', e.target.value)} placeholder="mínimo 6 caracteres" required />
      <Input label="Nombre de la tienda" value={f.storeName} onChange={e => set('storeName', e.target.value)} placeholder="Zapatillas Luna" />
      <Input label="Vencimiento de suscripción" type="date" value={f.expiresAt} onChange={e => set('expiresAt', e.target.value)} />
      <div style={{ display:'flex',gap:10,justifyContent:'flex-end',marginTop:8 }}>
        <Btn variant="ghost" type="button" onClick={onClose}>Cancelar</Btn>
        <Btn type="submit" loading={saving}>Crear cliente</Btn>
      </div>
    </form>
  );
}

// ─── Edit expiry form ─────────────────────────────────────────────────────────

function EditForm({ client, onSave, onClose, saving }) {
  const [expiresAt, setExpiresAt] = useState(
    client.expiresAt ? client.expiresAt.substring(0, 10) : ''
  );
  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ expiresAt: expiresAt || null }); }}>
      <Input label="Fecha de vencimiento" type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
      <div style={{ display:'flex',gap:10,justifyContent:'flex-end' }}>
        <Btn variant="ghost" type="button" onClick={onClose}>Cancelar</Btn>
        <Btn type="submit" loading={saving}>Guardar</Btn>
      </div>
    </form>
  );
}

// ─── Change password form ─────────────────────────────────────────────────────

function PasswordForm({ client, onClose }) {
  const [f, setF] = useState({ newPassword:'', confirmPassword:'' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (f.newPassword !== f.confirmPassword) { toast('Las contraseñas no coinciden', 'error'); return; }
    setSaving(true);
    try {
      await changeClientPassword(client.id, f.newPassword, f.confirmPassword);
      toast('Contraseña actualizada ✅');
      onClose();
    } catch (err) { toast(err.message, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Input label="Nueva contraseña *" type="password" value={f.newPassword} onChange={e => set('newPassword', e.target.value)} required />
      <Input label="Confirmar contraseña *" type="password" value={f.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} required />
      <div style={{ display:'flex',gap:10,justifyContent:'flex-end' }}>
        <Btn variant="ghost" type="button" onClick={onClose}>Cancelar</Btn>
        <Btn type="submit" loading={saving}>Actualizar</Btn>
      </div>
    </form>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminClients() {
  const [clients, setClients]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editModal, setEditModal]  = useState(null);
  const [passModal, setPassModal]  = useState(null);
  const [saving, setSaving]        = useState(false);
  const { confirm, Dialog }        = useConfirm();

  const load = useCallback(() => {
    setLoading(true);
    getClients().then(setClients).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      await createClient(data);
      toast('Cliente creado ✅');
      setShowCreate(false);
      load();
    } catch (err) { toast(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (c) => {
    try {
      await updateClient(c.id, { active: !c.active });
      toast(c.active ? 'Tienda desactivada' : 'Tienda activada');
      load();
    } catch (err) { toast(err.message, 'error'); }
  };

  const handleEdit = async (data) => {
    setSaving(true);
    try {
      await updateClient(editModal.id, data);
      toast('Cliente actualizado ✅');
      setEditModal(null);
      load();
    } catch (err) { toast(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    const ok = await confirm(`¿Eliminar al cliente "${c.username}" y toda su tienda? Esta acción es irreversible.`);
    if (!ok) return;
    try {
      await deleteClient(c.id);
      toast('Cliente eliminado');
      load();
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <div>
      <Dialog />

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Nuevo cliente">
        <CreateForm onSave={handleCreate} onClose={() => setShowCreate(false)} saving={saving} />
      </Modal>

      <Modal open={!!editModal} onClose={() => setEditModal(null)} title={`Editar — ${editModal?.username}`} width={400}>
        {editModal && <EditForm client={editModal} onSave={handleEdit} onClose={() => setEditModal(null)} saving={saving} />}
      </Modal>

      <Modal open={!!passModal} onClose={() => setPassModal(null)} title={`Cambiar clave — ${passModal?.username}`} width={400}>
        {passModal && <PasswordForm client={passModal} onClose={() => setPassModal(null)} />}
      </Modal>

      {/* Header */}
      <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:24,flexWrap:'wrap',gap:12 }}>
        <div>
          <h1 style={{ color:'#fff',fontSize:24,fontFamily:"'Syne',sans-serif",fontWeight:800,margin:'0 0 5px' }}>Clientes</h1>
          <p style={{ color:'rgba(255,255,255,0.38)',fontSize:14,margin:0 }}>
            {clients.length} cliente{clients.length !== 1 ? 's' : ''} registrado{clients.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Btn onClick={() => setShowCreate(true)}>+ Nuevo cliente</Btn>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display:'flex',justifyContent:'center',padding:60 }}><Spinner /></div>
      ) : clients.length === 0 ? (
        <Card>
          <div style={{ textAlign:'center',padding:'40px 20px' }}>
            <div style={{ fontSize:52,marginBottom:12 }}>👥</div>
            <p style={{ color:'rgba(255,255,255,0.4)',marginBottom:16 }}>Todavía no tenés clientes.</p>
            <Btn onClick={() => setShowCreate(true)}>+ Crear el primero</Btn>
          </div>
        </Card>
      ) : (
        <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
          {clients.map(c => {
            const st = clientStatus(c);
            return (
              <Card key={c.id} style={{ padding:'16px 20px' }}>
                <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,flexWrap:'wrap' }}>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:6,flexWrap:'wrap' }}>
                      <span style={{ color:'#fff',fontWeight:600,fontSize:15 }}>{c.username}</span>
                      <Badge color={st.color}>{st.label}</Badge>
                    </div>
                    <div style={{ display:'flex',gap:18,flexWrap:'wrap',marginBottom:6 }}>
                      <span style={{ color:'rgba(255,255,255,0.35)',fontSize:12 }}>
                        Creado: {new Date(c.createdAt).toLocaleDateString('es-AR')}
                      </span>
                      {c.expiresAt && (
                        <span style={{ color: st.color === 'yellow' ? '#fde047' : 'rgba(255,255,255,0.35)', fontSize:12 }}>
                          Vence: {new Date(c.expiresAt).toLocaleDateString('es-AR')}
                        </span>
                      )}
                    </div>
                    <a
                      href={storeUrl(c.id)}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color:'rgba(124,58,237,0.75)',fontSize:12,textDecoration:'none' }}
                    >
                      🔗 Ver tienda pública
                    </a>
                  </div>

                  <div style={{ display:'flex',gap:7,flexWrap:'wrap',alignItems:'center' }}>
                    <Btn size="sm" variant={c.active ? 'warning' : 'success'} onClick={() => handleToggleActive(c)}>
                      {c.active ? '⏸ Desactivar' : '▶ Activar'}
                    </Btn>
                    <Btn size="sm" variant="ghost" onClick={() => setEditModal(c)}>📅 Vencimiento</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => setPassModal(c)}>🔑 Clave</Btn>
                    <Btn size="sm" variant="danger" onClick={() => handleDelete(c)}>🗑</Btn>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
